import { useEffect } from 'react';

import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import styled from 'styled-components';

import { SpacedButton } from 'chime-ui/components/Button';

import Layout from 'components/Layout';
import StepHeader from 'components/StepHeader';

import pageTitles from 'consts/pageTitleConsts';

import useRoutes from 'hooks/useRoutes';

import {
  selectActiveAccountEmail,
  selectActiveAccountLoginToken,
} from 'selectors/enrollment';
import { selectUserProgress } from 'selectors/user';

import { DescriptionText, PageTitle } from 'styles/styleMixins';

import { acceptDismiss, dismissToLogin } from 'utils/linkHelper';
import { buildChimeWebLink, chimeWebHost } from 'utils/linkHelper';
import { IS_EMBEDDED } from 'utils/queryParamHelpers';
import doRedirect from 'utils/redirectHelper';
import { alreadyHasActiveAccountPageView } from 'utils/segment/pageEvents';
import { trackLoginRedirectClickEvent } from 'utils/segment/trackingEvents';
import sendPostMessage from 'utils/sendPostMessage';
import sendReactNativePostMessage from 'utils/sendReactNativePostMessage';

const AlreadyHasActiveAccount = () => {
  const userProgress = useSelector(selectUserProgress);
  const { getFirstPathFromRouteFlow } = useRoutes();
  const initialPagePath = userProgress || getFirstPathFromRouteFlow();
  const activeAccountEmail = useSelector(selectActiveAccountEmail);
  const activeAccountLoginToken = useSelector(selectActiveAccountLoginToken);
  const gotoNativeLogin = () => {
    trackLoginRedirectClickEvent('Already Has Active Account');

    const postMessageData = {
      step: 'already_has_active_account',
      email: activeAccountEmail,
      viewBanner: 'BANNER_NAME_UPDATE_EMAIL',
      ...(Boolean(activeAccountLoginToken) && {
        token: activeAccountLoginToken,
      }),
    };

    sendReactNativePostMessage(postMessageData);

    return postMessageData?.token
      ? doRedirect(acceptDismiss, {})
      : doRedirect(dismissToLogin, {});
  };

  useEffect(() => {
    alreadyHasActiveAccountPageView();

    const handleMessage = event => {
      if (event.origin !== chimeWebHost()) {
        return;
      }

      let eventData;
      try {
        eventData = JSON.parse(event.data);
      } catch (e) {
        return;
      }

      if (eventData.eventType === 'loaded') {
        sendPostMessage(
          event.source,
          {
            step: 'already_has_active_account',
            email: activeAccountEmail,
            viewBanner: 'BANNER_NAME_UPDATE_EMAIL',
          },
          chimeWebHost()
        );
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  const redirectIfEmailIsMissing = () => {
    if (activeAccountEmail) return null;
    return <Redirect to={initialPagePath} />;
  };

  return (
    <Layout>
      {redirectIfEmailIsMissing()}
      <main role="main">
        <StepHeader>
          <PageTitle>{pageTitles.ALREADY_HAS_ACTIVE_ACCOUNT_PAGE}</PageTitle>
          <DescriptionText>Log in with the email below.</DescriptionText>
        </StepHeader>

        <Label>Email</Label>
        <EmailField>{activeAccountEmail}</EmailField>
        {IS_EMBEDDED ? (
          <SpacedButton onClick={gotoNativeLogin}>Login</SpacedButton>
        ) : (
          <SpacedButton
            as="a"
            href={buildChimeWebLink('/login')}
            rel="opener"
            target="chime_login_window"
            onClick={() =>
              trackLoginRedirectClickEvent('Already Has Active Account')
            }
          >
            Login
          </SpacedButton>
        )}
      </main>
    </Layout>
  );
};

AlreadyHasActiveAccount.propTypes = {
  routeObj: PropTypes.shape({
    index: PropTypes.number,
    name: PropTypes.string,
    path: PropTypes.string,
  }),
};

export default AlreadyHasActiveAccount;

const Label = styled(DescriptionText)`
  font-size: 12px;
  margin-bottom: 0;
`;

const EmailField = styled(DescriptionText)`
  margin-top: 0;
`;
